import { useState } from 'react';
import {
  Card,
  Table,
  Tag,
  Space,
  Button,
  Modal,
  Form,
  Input,
  DatePicker,
  message,
} from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { api } from '../api/client';

const PHASE_COLOR: Record<string, string> = {
  DRAFT: 'default',
  ONGOING: 'processing',
  CALCULATING: 'warning',
  CLOSED: 'success',
};

const PHASE_LABEL: Record<string, string> = {
  DRAFT: '草稿',
  ONGOING: '进行中',
  CALCULATING: '核算中',
  CLOSED: '已结束',
  COMPLETED: '已完成',
  LOCKED: '已锁定',
};

export function BatchesPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const [form] = Form.useForm();

  const { data = [] } = useQuery({
    queryKey: ['batches'],
    queryFn: () => api.get('/admin/batches'),
  });

  const createMutation = useMutation({
    mutationFn: (vals: any) =>
      api.post('/admin/batches', {
        name: vals.name,
        displayName: vals.displayName?.trim() || undefined,
        startAt: vals.range[0].toISOString(),
        endAt: vals.range[1].toISOString(),
        performancePeriodStart: vals.periodRange?.[0]?.format('YYYY-MM'),
        performancePeriodEnd: vals.periodRange?.[1]?.format('YYYY-MM'),
      }),
    onError: (e: any) => message.error(e?.response?.data?.message || e?.message || '创建失败'),
    onSuccess: () => {
      message.success('已创建');
      setModalOpen(false);
      form.resetFields();
      qc.invalidateQueries({ queryKey: ['batches'] });
    },
  });

  return (
    <div>
      <div className="tg-page-title">盘点批次</div>
      <Card
        extra={
          <Button type="primary" onClick={() => setModalOpen(true)}>
            新建批次
          </Button>
        }
      >
        <Table
          rowKey="id"
          dataSource={data as any[]}
          columns={[
            { title: 'ID', dataIndex: 'id', width: 60 },
            {
              title: '名称',
              render: (_, r: any) =>
                r.displayName ? (
                  <Space size={6}>
                    <Tag color="purple">{r.displayName}</Tag>
                    <span style={{ color: '#999', fontSize: 12 }}>({r.name})</span>
                  </Space>
                ) : (
                  r.name
                ),
            },
            {
              title: '阶段',
              dataIndex: 'phase',
              render: (v) => <Tag color={PHASE_COLOR[v]}>{PHASE_LABEL[v] || v}</Tag>,
            },
            {
              title: '起止',
              render: (_, r) => `${dayjs(r.startAt).format('MM-DD')} ~ ${dayjs(r.endAt).format('MM-DD')}`,
            },
            {
              title: '操作',
              render: (_, r) => (
                <Space>
                  <Button size="small" onClick={() => navigate(`/batches/${r.id}`)}>详情</Button>
                </Space>
              ),
            },
          ]}
        />
      </Card>

      <Modal
        title="新建批次"
        open={modalOpen}
        onOk={() => form.submit()}
        onCancel={() => setModalOpen(false)}
        confirmLoading={createMutation.isPending}
      >
        <Form form={form} layout="vertical" onFinish={(v) => createMutation.mutate(v)}>
          <Form.Item
            label="批次技术名"
            name="name"
            rules={[{ required: true }]}
            extra="短代号，全局唯一，用于程序内查找/PDF文件名/日志（如 2026H1），保存后不建议修改。"
          >
            <Input placeholder="如 2026H1" />
          </Form.Item>
          <Form.Item
            label="批次展示名"
            name="displayName"
            extra="业务友好名（如「2026年上半年晋升提升」）。前端各下拉、Banner 一律用此名，留空则回退技术名。"
          >
            <Input placeholder="如 2026年上半年晋升提升" />
          </Form.Item>
          <Form.Item label="起止时间" name="range" rules={[{ required: true }]}>
            <DatePicker.RangePicker showTime style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            label="盘点区间（绩效业绩）· 起止月"
            name="periodRange"
            extra="绩效业绩矩阵的月份列（连续，最多 12 个月）。留空则默认近 6 个月（含当月）。"
          >
            <DatePicker.RangePicker picker="month" style={{ width: '100%' }} placeholder={['起始月', '结束月']} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
